import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Lock, LockOpen, ShieldCheck, ShieldAlert, Mail, Loader2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

const ROLE_LABEL = (u) => u.platform_role === "admin" ? "Platform Admin" : (u.tenant_role ? u.tenant_role.charAt(0).toUpperCase() + u.tenant_role.slice(1) : "—");

const EVENT_LABEL = {
  login_success: "Login", login_failed: "Failed login", account_locked: "Account locked",
  account_unlocked: "Account unlocked", password_reset_requested: "Reset requested",
  password_reset_completed: "Password reset", logout: "Logout",
};

export default function PlatformUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/platform/users").then((r) => setUsers(r.data));
    api.get("/platform/auth-logs?limit=100").then((r) => setLogs(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.email) setTestEmail((e) => e || user.email); }, [user]);

  const sendTest = async (e) => {
    e.preventDefault();
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/platform/test-email", { email: testEmail });
      setResult({ status: data.status, message: data.message });
      if (data.status === "sent") toast.success(data.message);
      load();
    } catch (err) {
      const status = err.response?.status;
      const msg = status === 429
        ? "Too many test emails. Please wait a few minutes and try again."
        : formatApiError(err.response?.data?.detail) || "Could not send test email.";
      setResult({ status: "error", message: msg });
    } finally {
      setSending(false);
    }
  };

  const unlock = async (u) => {
    try {
      await api.post(`/platform/users/${u.id}/unlock`);
      toast.success(`Unlocked ${u.email}`);
      load();
    } catch { toast.error("Could not unlock account"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Users &amp; Security</h1>
        <p className="text-muted-foreground mt-1">Everyone across the platform, with authentication controls.</p>
      </div>

      <Card className="rounded-xl border-border p-6" data-testid="test-email-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-primary" /></div>
          <div className="flex-1">
            <h2 className="text-base font-bold tracking-tight">Email delivery test</h2>
            <p className="text-muted-foreground text-sm mt-0.5">Send a branded test email through the configured provider to verify deliverability.</p>
            <form onSubmit={sendTest} className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="test-email">Destination email</Label>
                <Input id="test-email" type="email" required value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="admin@drelik.com" className="h-11" data-testid="test-email-input" />
              </div>
              <Button type="submit" disabled={sending} className="h-11 rounded-lg" data-testid="test-email-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send test email"}
              </Button>
            </form>
            {result && (
              <div data-testid="test-email-result" className={`mt-3 flex items-center gap-2 text-sm ${result.status === "sent" ? "text-emerald-600" : result.status === "not_configured" ? "text-amber-600" : "text-destructive"}`}>
                {result.status === "sent" ? <CheckCircle2 className="h-4 w-4" /> : result.status === "not_configured" ? <AlertTriangle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {result.message}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="rounded-xl border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Storefront</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant="secondary" className="rounded-full border-0">{ROLE_LABEL(u)}</Badge></TableCell>
                <TableCell className="text-sm">{u.storefront_name || "—"}</TableCell>
                <TableCell>
                  {u.locked
                    ? <span className="inline-flex items-center gap-1 text-sm text-destructive font-medium" data-testid={`user-locked-${u.id}`}><Lock className="h-3.5 w-3.5" /> Locked</span>
                    : <span className="inline-flex items-center gap-1 text-sm text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Active</span>}
                </TableCell>
                <TableCell className="text-right">
                  {u.locked && <Button size="sm" variant="outline" className="rounded-lg" onClick={() => unlock(u)} data-testid={`unlock-btn-${u.id}`}><LockOpen className="h-3.5 w-3.5 mr-1.5" /> Unlock</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div>
        <h2 className="text-lg font-bold tracking-tight flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-muted-foreground" /> Authentication activity</h2>
        <p className="text-muted-foreground text-sm mt-0.5">Recent sign-in and account-security events.</p>
      </div>
      <Card className="rounded-xl border-border overflow-hidden" data-testid="auth-logs-card">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Event</TableHead><TableHead>Email</TableHead><TableHead>Result</TableHead><TableHead>IP</TableHead><TableHead>When</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No authentication events yet.</TableCell></TableRow>}
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`auth-log-${l.id}`}>
                <TableCell className="font-medium text-sm">{EVENT_LABEL[l.event] || l.event}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.email || "—"}</TableCell>
                <TableCell>{l.success ? <Badge variant="secondary" className="rounded-full border-0 text-emerald-600">Success</Badge> : <Badge variant="secondary" className="rounded-full border-0 text-destructive">Failed</Badge>}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.ip || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
